// Device trust (M73, docs/device-trust.md): this device's own record of
// everyone's devices (D300), the check a wrapped key must pass (D301), and
// what the record says about a person (D302, D303, D304). Pure functions on
// plain data, so the shared vector (D305,
// shared/contracts/device-trust-vector.json) can drive them directly;
// session.ts keeps the record in IndexedDB and calls these.
import { pqLearnDecision } from './pq';

/** One device public key as this device first learned it (D300). */
export interface KnownDevice {
  /** When this device first saw the key (ms since 1970). */
  firstSeenAt: number;
  /** The server's id for the key, when the answer gave one. */
  id?: string;
  /**
   * A complete list for its owner no longer had it (ms). Kept rather than
   * deleted: it is still accepted as the wrapper of an epoch this device had
   * seen before then (the watermark, D301), but no longer counts for
   * wrapping or the code (D302).
   */
  removedAt?: number;
  /**
   * The device's ML-KEM-768 key (M78, D338): the first one a keys answer
   * gave for this P-256 key, learned silently, never replaced. Wrapping
   * uses it and the security code covers it.
   */
  mlkem?: string;
}

/** Someone as a member of one chat or family, as this app has seen them. */
export interface KnownMember {
  /** First seen as a member (ms). */
  since: number;
  /** First seen no longer a member (ms); unset while they are one. */
  leftAt?: number;
}

export interface TrustRecord {
  v: 1;
  /** When the record was started on this device. */
  startedAt: number;
  /**
   * This device had its key before the record existed (the first run after
   * M73). Keys wrapped before anyone kept a record, some by devices that are
   * gone, are accepted on the first look at a scope that was in the first
   * lists loaded after the record started (`lookScopes`, D304).
   */
  legacy: boolean;
  /** userId → public key → what was seen. */
  people: Record<string, Record<string, KnownDevice>>;
  /**
   * userId → when this device last noticed them gain a device (D303). My own
   * entry is a new device on my account (not this browser's own key).
   */
  notices: Record<string, number>;
  /** When I last saw the notice about my own devices. */
  ownSeenAt?: number;
  /** `<kind>:<id>:<epoch>` → the wrapper of the copy that opened. */
  accepted: Record<string, string>;
  /** `<kind>:<id>` → this device has looked at that scope's keys. */
  seen: Record<string, true>;
  /**
   * `<kind>:<id>` → userId → membership, from the chat's participants or the
   * family's members as the app shows them (never from a keys answer).
   */
  members: Record<string, Record<string, KnownMember>>;
  /**
   * `<kind>:<id>` → userId → when this device noticed them join the scope
   * after its first look at who was in it (D343): the quiet "{Name} was
   * added to this chat" / "…joined the family" line. The newest per person;
   * someone removed and added again is noticed again.
   */
  joined: Record<string, Record<string, number>>;
  /**
   * `<kind>:<id>` → the epoch watermark: each time this device saw a higher
   * epoch for the scope, that epoch and when (this device's clock). The
   * device's own evidence of which epochs existed when; the server's epoch
   * times are only sanity-checked (D301).
   */
  watermarks: Record<string, Array<{ epoch: number; at: number }>>;
  /**
   * Scopes that may have a first look (D304): the families in the first
   * family list loaded after a legacy record started, and the chats in each
   * such family's first chat list. Set once; a later scope never gets one.
   */
  lookScopes: Record<string, true>;
  /** The first family list's ids, once taken (legacy records only). */
  lookFamilies?: string[];
  /** Families whose first chat list was taken for `lookScopes`. */
  lookChatsFor: Record<string, true>;
  /**
   * `<kind>:<id>` → when that chat's or family's encryption began, as this
   * device knows it (D347, ms): the server's `encryptedSince` the first
   * time this device saw the scope, lowered to the time of anything sealed
   * this device opened there, never raised. Plaintext dated from then on
   * is not shown.
   */
  plainBefore?: Record<string, number>;
}

/** A device as a keys answer lists it: whose, its id, its public key. */
export interface ListedDevice {
  userId: string;
  id?: string;
  publicKey: string;
  /**
   * Its ML-KEM key as the answer gives it (M78): null when the answer says
   * it has none, undefined when the answer doesn't say (an older server or
   * an offline copy). A key failing FIPS 203's check arrives as null.
   */
  mlkemPublicKey?: string | null;
}

/** A recorded device whose answer disagreed about its ML-KEM key (D338). */
export interface PqKeyNote {
  count: 'e2ee_pq_key_changed' | 'e2ee_pq_key_missing';
  publicKey: string;
}

export function newTrustRecord(now: number, legacy: boolean): TrustRecord {
  return {
    v: 1,
    startedAt: now,
    legacy,
    people: {},
    notices: {},
    accepted: {},
    seen: {},
    members: {},
    joined: {},
    watermarks: {},
    lookScopes: {},
    lookChatsFor: {},
  };
}

/** A stored record, with any field a newer build added filled in. */
export function normalizeTrustRecord(stored: unknown): TrustRecord | null {
  const record = stored as Partial<TrustRecord> | null;
  if (!record || record.v !== 1 || typeof record.startedAt !== 'number') {
    return null;
  }
  return {
    ...newTrustRecord(record.startedAt, Boolean(record.legacy)),
    ...record,
    people: record.people ?? {},
    notices: record.notices ?? {},
    accepted: record.accepted ?? {},
    seen: record.seen ?? {},
    members: record.members ?? {},
    joined: record.joined ?? {},
    watermarks: record.watermarks ?? {},
    lookScopes: record.lookScopes ?? {},
    lookChatsFor: record.lookChatsFor ?? {},
  } as TrustRecord;
}

/**
 * Learns the devices a keys answer lists (D300). `complete`: the answer
 * lists every device of each person in it (a chat's or family's devices,
 * a person's devices), so a recorded key that is missing went away, and one
 * that is back comes back; a partial answer (an offline copy) only adds.
 *
 * `gained`: people this record already knew who now have a key it had never
 * seen (D303). Someone new to the record is learned silently (D304), and so
 * is a key that comes back after being removed. My own gain is `mine`, not
 * in `gained`, and never for `ownPublicKey` (the key this browser holds).
 * `completeFor`: people the complete answer covers even if it lists none of
 * their devices (a chat's members, the person asked about).
 */
export function mergeDevices(
  record: TrustRecord,
  me: string,
  listed: readonly ListedDevice[],
  now: number,
  complete: boolean,
  ownPublicKey?: string,
  completeFor: readonly string[] = []
): {
  record: TrustRecord;
  gained: string[];
  mine: boolean;
  /** Answers that named another ML-KEM key, or none, for a recorded one. */
  pqNotes: PqKeyNote[];
} {
  const byUser = new Map<string, ListedDevice[]>();
  for (const device of listed) {
    if (!device.userId || !device.publicKey) continue;
    byUser.set(device.userId, [...(byUser.get(device.userId) ?? []), device]);
  }
  // A complete answer covers people with no devices left too (signed out
  // of the last one, D55): their recorded keys are removed, not kept current.
  if (complete) {
    for (const user of completeFor) {
      if (!byUser.has(user) && record.people[user]) byUser.set(user, []);
    }
  }
  const people = { ...record.people };
  const notices = { ...record.notices };
  const gained: string[] = [];
  const pqNotes: PqKeyNote[] = [];
  let mine = false;
  for (const [user, devices] of byUser) {
    const before = people[user];
    const keys: Record<string, KnownDevice> = { ...(before ?? {}) };
    let gainedHere = false;
    const present = new Set<string>();
    for (const device of devices) {
      present.add(device.publicKey);
      const known = keys[device.publicKey];
      const listedMlkem = device.mlkemPublicKey;
      if (!known) {
        // A new device: both keys recorded together, announced as today.
        keys[device.publicKey] = {
          firstSeenAt: now,
          ...(device.id ? { id: device.id } : {}),
          ...(listedMlkem ? { mlkem: listedMlkem } : {}),
        };
        if (before && !(user === me && device.publicKey === ownPublicKey)) {
          gainedHere = true;
        }
        continue;
      }
      // Its ML-KEM key (D338, vector learnCases): learned silently for a
      // recorded, current P-256 key; never replaced; ignored for a removed
      // one (a key coming back is D300's business). Only when the answer
      // says something about it.
      let learn: string | undefined;
      if (listedMlkem !== undefined) {
        const decision = pqLearnDecision({
          p256: known.removedAt === undefined ? 'current' : 'removed',
          recordedMlkem: known.mlkem ?? null,
          answerMlkem: listedMlkem,
        });
        if (decision === 'learn-silently') learn = listedMlkem ?? undefined;
        else if (decision === 'keep-recorded-changed') {
          pqNotes.push({
            count: 'e2ee_pq_key_changed',
            publicKey: device.publicKey,
          });
        } else if (decision === 'keep-recorded-missing') {
          pqNotes.push({
            count: 'e2ee_pq_key_missing',
            publicKey: device.publicKey,
          });
        }
      }
      // Back after being removed: only a complete answer says so.
      const back = complete && known.removedAt !== undefined;
      const newId = device.id !== undefined && known.id === undefined;
      if (back || newId || learn) {
        const { removedAt, ...kept } = known;
        keys[device.publicKey] = {
          ...kept,
          ...(back || removedAt === undefined ? {} : { removedAt }),
          ...(newId ? { id: device.id } : {}),
          ...(learn ? { mlkem: learn } : {}),
        };
      }
    }
    if (complete) {
      for (const [publicKey, known] of Object.entries(keys)) {
        if (!present.has(publicKey) && known.removedAt === undefined) {
          keys[publicKey] = { ...known, removedAt: now };
        }
      }
    }
    people[user] = keys;
    if (gainedHere) {
      notices[user] = now;
      if (user === me) mine = true;
      else gained.push(user);
    }
  }
  // The same notices object when nothing was gained: the UI reads it as a
  // snapshot (useSyncExternalStore).
  const changed = gained.length > 0 || mine;
  return {
    record: { ...record, people, notices: changed ? notices : record.notices },
    gained,
    mine,
    pqNotes,
  };
}

/**
 * Records who is in a chat or family now (D301), from the participants or
 * members the app shows. Someone missing from a list they were on has left
 * (`leftAt`); someone back is a member again.
 *
 * Someone who joins after this device's first look at the scope's members
 * (anyone recorded before, in any state) is noticed in `joined` (D343): a
 * server could add an account to a chat or family, and devices would then
 * wrap keys for it. The first look is silent (D304), and so is someone
 * leaving; someone back after leaving is noticed again.
 */
export function noteMembers(
  record: TrustRecord,
  scope: string,
  userIds: readonly string[],
  now: number
): TrustRecord {
  const before = record.members[scope] ?? {};
  const firstLook = Object.keys(before).length === 0;
  const next: Record<string, KnownMember> = { ...before };
  const present = new Set(userIds);
  const joinedNow: string[] = [];
  for (const user of present) {
    const known = before[user];
    if (!known || known.leftAt !== undefined) {
      next[user] = { since: known?.since ?? now };
      if (!firstLook) joinedNow.push(user);
    }
  }
  for (const [user, known] of Object.entries(before)) {
    if (!present.has(user) && known.leftAt === undefined) {
      next[user] = { ...known, leftAt: now };
    }
  }
  const members = { ...record.members, [scope]: next };
  if (joinedNow.length === 0) return { ...record, members };
  const inScope = { ...(record.joined[scope] ?? {}) };
  for (const user of joinedNow) inScope[user] = now;
  return {
    ...record,
    members,
    joined: { ...record.joined, [scope]: inScope },
  };
}

/** Who is a member of the scope now, as recorded. */
export function currentMembers(
  record: TrustRecord,
  scope: string
): Set<string> {
  return new Set(
    Object.entries(record.members[scope] ?? {})
      .filter(([, known]) => known.leftAt === undefined)
      .map(([user]) => user)
  );
}

/** Why a wrapped key was accepted, or that it was refused (D301). */
export type WrapperVerdict =
  /** Wrapped by one of my own devices. */
  | 'own'
  /** The wrapper of the copy this device opened for that epoch before. */
  | 'pinned'
  /** Wrapped by a recorded device of someone in the chat or family now. */
  | 'member'
  /** Wrapped by a recorded device of someone who left, for an epoch seen before. */
  | 'formerMember'
  /** A pre-M73 device's first look at a scope from its first lists (D304). */
  | 'firstLook'
  /**
   * Refused, but by someone who was (or is) in the scope, with a key that
   * was theirs: the epoch is above what this device had opened before the
   * key went or they left. Locked like a refusal; counted apart, as the
   * honest lockout it usually is (a device that never opened the scope).
   */
  | 'lapsed'
  | 'refused';

export interface WrapperCheck {
  /** `chat:<id>` or `family:<id>`. */
  scope: string;
  epoch: number;
  /**
   * When the epoch began (ms), from the keys answer's `epochs`, only when
   * those times passed `epochTimesSane`; null otherwise (an older server, a
   * copy saved before M73, or times that make no sense).
   */
  epochCreatedAt: number | null;
  /** The `wrappedByPublicKey` the server names. */
  wrapper: string;
  /**
   * Who is in the chat or family now, as the app knows it (participants or
   * members), never a keys answer's device owners alone.
   */
  members: readonly string[];
  me: string;
  /** This device's own public key. */
  ownPublicKey: string;
}

/**
 * The highest epoch this device had seen for the scope strictly before `at`
 * (its own clock); 0 when none.
 */
export function highestBefore(
  record: TrustRecord,
  scope: string,
  at: number
): number {
  let highest = 0;
  for (const mark of record.watermarks[scope] ?? []) {
    if (mark.at < at && mark.epoch > highest) highest = mark.epoch;
  }
  return highest;
}

/** Raises the scope's watermark when `epoch` is higher than any seen. */
export function noteEpoch(
  record: TrustRecord,
  scope: string,
  epoch: number,
  now: number
): TrustRecord {
  const marks = record.watermarks[scope] ?? [];
  const top = marks.reduce((max, mark) => Math.max(max, mark.epoch), 0);
  if (!(epoch > top)) return record;
  return {
    ...record,
    watermarks: {
      ...record.watermarks,
      [scope]: [...marks, { epoch, at: now }],
    },
  };
}

/** The highest epoch in the scope's watermark; 0 when none (D341). */
export function topEpoch(record: TrustRecord, scope: string): number {
  return (record.watermarks[scope] ?? []).reduce(
    (max, mark) => Math.max(max, mark.epoch),
    0
  );
}

/**
 * May new content be sealed in a scope whose current epoch the server says
 * is `currentEpoch` (D341)? Never with an epoch below the highest this
 * device has opened or made there: a dishonest server could name an epoch
 * from before someone was removed, whose key they still hold. `rollback`
 * is handled like STALE_KEY: the keys are asked for again, and if the
 * answer stays below, nothing is sent.
 */
export function checkSendEpoch(
  record: TrustRecord,
  scope: string,
  currentEpoch: number
): 'ok' | 'rollback' {
  return currentEpoch < topEpoch(record, scope) ? 'rollback' : 'ok';
}

/** Times this far ahead of this device's clock still pass (clock skew). */
export const EPOCH_TIME_SKEW_MS = 5 * 60 * 1000;

/**
 * Do a keys answer's epoch times make sense (D301)? Each a finite time, none
 * later than now (plus skew), and never going down as the epoch number goes
 * up. They only ever narrow what is accepted (the first look); nothing is
 * accepted because of them.
 */
export function epochTimesSane(
  epochs: ReadonlyArray<{ epoch: number; createdAt: number }>,
  now: number
): boolean {
  const sorted = [...epochs].sort((a, b) => a.epoch - b.epoch);
  let last = -Infinity;
  for (const entry of sorted) {
    if (!Number.isFinite(entry.createdAt)) return false;
    if (entry.createdAt > now + EPOCH_TIME_SKEW_MS) return false;
    if (entry.createdAt < last) return false;
    last = entry.createdAt;
  }
  return true;
}

/**
 * A recorded key counts for an epoch while it was current: always if it
 * hasn't been removed, otherwise only for an epoch at or below the highest
 * this device had seen in the scope before the removal (D301).
 */
function counts(
  record: TrustRecord,
  scope: string,
  epoch: number,
  known: KnownDevice | undefined
) {
  if (!known) return false;
  if (known.removedAt === undefined) return true;
  return epoch <= highestBefore(record, scope, known.removedAt);
}

/**
 * The wrapper check (D301), in this order: own, pinned, member,
 * formerMember, firstLook, otherwise refused.
 */
export function checkWrapper(
  record: TrustRecord,
  check: WrapperCheck
): WrapperVerdict {
  const { wrapper, me, scope, epoch } = check;
  const keyOf = (user: string) => record.people[user]?.[wrapper];
  if (
    wrapper === check.ownPublicKey ||
    counts(record, scope, epoch, keyOf(me))
  ) {
    return 'own';
  }
  if (record.accepted[`${scope}:${epoch}`] === wrapper) return 'pinned';
  const now = new Set(check.members);
  for (const member of now) {
    if (member !== me && counts(record, scope, epoch, keyOf(member))) {
      return 'member';
    }
  }
  for (const [user, known] of Object.entries(record.members[scope] ?? {})) {
    if (
      user !== me &&
      !now.has(user) &&
      known.leftAt !== undefined &&
      epoch <= highestBefore(record, scope, known.leftAt) &&
      counts(record, scope, epoch, keyOf(user))
    ) {
      return 'formerMember';
    }
  }
  const at = check.epochCreatedAt;
  if (
    record.legacy &&
    record.lookScopes[scope] &&
    !record.seen[scope] &&
    at !== null &&
    at < record.startedAt
  ) {
    return 'firstLook';
  }
  // Someone's key, recorded for them while in the scope, just not for this
  // epoch: an honest lockout more often than an attack (D301).
  const mineOrTheirs = [
    me,
    ...now,
    ...Object.keys(record.members[scope] ?? {}),
  ];
  if (mineOrTheirs.some((user) => keyOf(user) !== undefined)) return 'lapsed';
  return 'refused';
}

/**
 * Raises the scope's watermark from the epochs whose copies this device
 * actually opened (or holds) in a look, never from the epoch numbers an
 * answer claims (`currentEpoch`, its `epochs`): a server could claim a huge
 * epoch while someone is still a member, and every epoch up to it would
 * then pass for them after they leave (D301, D58).
 */
export function raiseWatermark(
  record: TrustRecord,
  scope: string,
  openedEpochs: readonly number[],
  now: number
): TrustRecord {
  const top = openedEpochs.reduce((max, epoch) => Math.max(max, epoch), 0);
  return top > 0 ? noteEpoch(record, scope, top, now) : record;
}

/**
 * The first family list loaded after a legacy record started (D304): its
 * families may have a first look. Taken once; nothing for other records.
 */
export function noteFamilyList(
  record: TrustRecord,
  familyIds: readonly string[]
): TrustRecord {
  if (!record.legacy || record.lookFamilies !== undefined) return record;
  const lookScopes = { ...record.lookScopes };
  for (const id of familyIds) lookScopes[`family:${id}`] = true;
  return { ...record, lookFamilies: [...familyIds], lookScopes };
}

/**
 * A family's first chat list after a legacy record started (D304): its
 * chats may have a first look, if the family was in the first family list.
 * Taken once per family.
 */
export function noteChatList(
  record: TrustRecord,
  familyId: string,
  chatIds: readonly string[]
): TrustRecord {
  if (
    !record.legacy ||
    !record.lookFamilies?.includes(familyId) ||
    record.lookChatsFor[familyId]
  ) {
    return record;
  }
  const lookScopes = { ...record.lookScopes };
  for (const id of chatIds) lookScopes[`chat:${id}`] = true;
  return {
    ...record,
    lookScopes,
    lookChatsFor: { ...record.lookChatsFor, [familyId]: true },
  };
}

/** A person's current keys in the record (D302), sorted. */
export function activeKeys(record: TrustRecord, user: string): string[] {
  return Object.entries(record.people[user] ?? {})
    .filter(([, known]) => known.removedAt === undefined)
    .map(([publicKey]) => publicKey)
    .sort();
}

/**
 * A person's current recorded devices with their ML-KEM keys (D338), for
 * the security code: one line each, `<P-256>` or `<P-256> <ML-KEM>`.
 */
export function activeDevices(
  record: TrustRecord,
  user: string
): Array<{ publicKey: string; mlkem: string | null }> {
  return Object.entries(record.people[user] ?? {})
    .filter(([, known]) => known.removedAt === undefined)
    .map(([publicKey, known]) => ({ publicKey, mlkem: known.mlkem ?? null }))
    .sort((a, b) => (a.publicKey < b.publicKey ? -1 : 1));
}

/** The ML-KEM key recorded for this person's device (D338), or null. */
export function recordedMlkem(
  record: TrustRecord,
  user: string,
  publicKey: string
): string | null {
  return record.people[user]?.[publicKey]?.mlkem ?? null;
}

/**
 * Does the record show an ML-KEM key for the device with this P-256 key,
 * whoever's it is (D339: a v1 copy from such a wrapper is a downgrade
 * above the PQ ceiling)? Removed devices count: their key was theirs.
 */
export function wrapperHasMlkem(
  record: TrustRecord,
  publicKey: string
): boolean {
  for (const devices of Object.values(record.people)) {
    if (devices[publicKey]?.mlkem) return true;
  }
  return false;
}

/** Is this public key a current recorded device of this person (D302)? */
export function isRecorded(
  record: TrustRecord,
  user: string,
  publicKey: string
): boolean {
  const known = record.people[user]?.[publicKey];
  return known !== undefined && known.removedAt === undefined;
}

/**
 * After a look at a scope's keys: every wrapper whose copy opened is pinned
 * for its epoch, so it stays accepted whoever leaves later (D301). The
 * scope is marked seen — the first look is over (D304) — only when the
 * answer said when its epochs began (`epochsKnown`), so a look at an older
 * server's answer never uses it up. A scope with no keys yet is seen too.
 */
export function afterLook(
  record: TrustRecord,
  scope: string,
  pins: ReadonlyArray<{ epoch: number; wrapper: string }>,
  epochsKnown: boolean
): TrustRecord {
  const unpinned = pins.filter(
    (pin) => record.accepted[`${scope}:${pin.epoch}`] !== pin.wrapper
  );
  // Only a scope that may have a first look can use it up: a look before
  // the lists name it (their own load opens their last messages) mustn't.
  const see = epochsKnown && !record.seen[scope] && !!record.lookScopes[scope];
  if (unpinned.length === 0 && !see) return record;
  const accepted = { ...record.accepted };
  for (const pin of unpinned) accepted[`${scope}:${pin.epoch}`] = pin.wrapper;
  return {
    ...record,
    accepted,
    seen: see ? { ...record.seen, [scope]: true } : record.seen,
  };
}

/** The notice about my own devices, when there is one I haven't seen. */
export function ownNotice(record: TrustRecord, me: string): number | null {
  const at = record.notices[me];
  if (at === undefined) return null;
  return at > (record.ownSeenAt ?? 0) ? at : null;
}

// ---- Plaintext only from before encryption (D347) -------------------------

/**
 * Notes when a scope's encryption began (D347). `server`: the keys answer's
 * `encryptedSince`, taken only if this device has nothing for the scope yet.
 * `opened`: the time of something sealed this device opened there, which
 * only ever lowers what is recorded. Never raised: a server that later
 * moves `encryptedSince` on gains nothing.
 */
export function notePlainBefore(
  record: TrustRecord,
  scope: string,
  at: number,
  source: 'server' | 'opened'
): TrustRecord {
  if (!Number.isFinite(at)) return record;
  const current = record.plainBefore?.[scope];
  if (current !== undefined && (source === 'server' || at >= current)) {
    return record;
  }
  return {
    ...record,
    plainBefore: { ...(record.plainBefore ?? {}), [scope]: at },
  };
}

/**
 * Whether something with no encryption, dated `createdAt` (ms), may be shown
 * as plain text in `scope` (D347): `show` before the scope's encryption
 * began, `refuse` from then on (or with no usable date), `unknown` while
 * this device knows of no encryption there (a chat that never started it:
 * all of it is from before M28).
 */
export function plainDecision(
  record: TrustRecord | null,
  scope: string,
  createdAt: number
): 'show' | 'refuse' | 'unknown' {
  const before = record?.plainBefore?.[scope];
  if (before === undefined) return 'unknown';
  return Number.isFinite(createdAt) && createdAt < before ? 'show' : 'refuse';
}
